import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { map, tap } from 'rxjs/operators';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { environment } from 'projects/workspace/src/environments/environment';
import { AppState } from '../../store/state/app.state';
import { selectCompanyProfile } from '../../administration/store/selectors';
import { CompanyProfile } from '../../administration/models/company-profile.model';
import { DisplayToaster } from '../../shared/decorators/toaster';
import { ResponseModel } from '../../shared/models';
import { PdfSettingsModel, PdfSettingsTemplateModel } from '../models';
import { PdfSettingsCornerStyleEnum, PdfSettingsRoundingEnum, PdfSettingsHeaderInfoBlocksEnum } from '../enums';
import { AdministrationsApiService } from '../../administration/services/administrations-api.service';
import { PositionGroupingEnum } from '../../shared/enums';
import { createStrictQueryParams } from '../../shared/helpers';

@Injectable({
  providedIn: 'root'
})
export class PdfSettingsApiService {

  private currentPdfSettings$: BehaviorSubject<PdfSettingsModel> = new BehaviorSubject<PdfSettingsModel>(null);

  private readonly apiEndpoint: string = `${environment.javaApiVersion}/workspaces`;
  private wid: number;
  public companyProfile: CompanyProfile;

  private apiUrl(url: string = ''): string {
    return this.apiEndpoint + `/${this.wid}/settings/pdf` + url;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<AppState>,
    private readonly toasterService: ToasterService,
    private readonly administrationsApiService: AdministrationsApiService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.store.select(selectCompanyProfile)
      .subscribe((profile: CompanyProfile) => {
        this.wid = profile.workspaceId;
        this.companyProfile = profile;
      });
  }

  @DisplayToaster({showErrorMessage: true})
  // CURRENT - for pdf settings, PUBLISHED - for pdf view
  public getPdfSettings(state:  'PUBLISHED'|'CURRENT' = 'CURRENT', name?: string, skipSelection = false): Observable<PdfSettingsModel> {
    const params = { };
    if (name) { params['name'] = name; }
    if (skipSelection) { params['skipSelection'] = skipSelection; }
    return this.http.get<ResponseModel<PdfSettingsModel>>(this.apiUrl(`/${state}`), { params: createStrictQueryParams(params)})
      .pipe(
        tap((res: ResponseModel<PdfSettingsModel>) => {
          this.setStyles(res.data);
          if (state === 'CURRENT') {
            this.setCurrentPdfSettings(res.data)
          }
        }),
        map((res: ResponseModel<PdfSettingsModel>) => res.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public restorePdfSettings(name?: string): Observable<PdfSettingsModel> {
    const params = { };
    if (name) { params['name'] = name; }
    return this.http.request<ResponseModel<PdfSettingsModel>>('post', this.apiUrl('/restore'), { params: createStrictQueryParams(params)})
      .pipe(
        tap((res: ResponseModel<PdfSettingsModel>) => {
          this.setStyles(res.data);
          this.setCurrentPdfSettings(res.data);
        }),
        map((res: ResponseModel<PdfSettingsModel>) => res.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public publishPdfSettings(name?: string): Observable<PdfSettingsModel> {
    const params = { };
    if (name) { params['name'] = name; }
    return this.http.request<ResponseModel<PdfSettingsModel>>('post', this.apiUrl('/publish'), { params: createStrictQueryParams(params)})
      .pipe(
        tap((res: ResponseModel<PdfSettingsModel>) => {
          this.setStyles(res.data);
          this.setCurrentPdfSettings(res.data);
        }),
        map((res: ResponseModel<PdfSettingsModel>) => res.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public updatePdfSettings(settings: PdfSettingsModel, name?: string): Observable<PdfSettingsModel> {
    const params = { };
    if (name) { params['name'] = name; }
    return this.http.post<ResponseModel<PdfSettingsModel>>(this.apiUrl('/current'), {...settings}, { params: createStrictQueryParams(params)})
      .pipe(
        tap((res: ResponseModel<PdfSettingsModel>) => {
          this.setStyles(res.data);
          this.setCurrentPdfSettings(res.data);
        }),
        tap(() => {
          if (!this.companyProfile.onboardingCompleted) {
            this.administrationsApiService.getOnboardingProcess().subscribe();
          }
        }),
        map((res: ResponseModel<PdfSettingsModel>) => res.data)
      );
  }

  public savePdfSettingsTemplate(body: { name?: string; setAsDefault?: boolean; basedOn: string; rewrite?: boolean }): Observable<PdfSettingsModel> {
    return this.http.request<ResponseModel<PdfSettingsModel>>('post', this.apiUrl('/template'), { body })
      .pipe(
        tap((res: ResponseModel<PdfSettingsModel>) => {
          this.setStyles(res.data);
          this.setCurrentPdfSettings(res.data);
        }),
        map((res: ResponseModel<PdfSettingsModel>) => res.data)
      );
  }

  public getCurrentPositionsGrouping(): Observable<PositionGroupingEnum> {
    return this.http.get<ResponseModel<PositionGroupingEnum>>(`${environment.javaApiVersion}/settings/${this.wid}/grouping-option/current`)
      .pipe(
        map((res: ResponseModel<PositionGroupingEnum>) => res.data)
      );
  }

  public updatePositionsGrouping(groupingOption: PositionGroupingEnum): Observable<any> {
    const params = { groupingOption };
    return this.http.request<ResponseModel<any>>('post', `${environment.javaApiVersion}/settings/${this.wid}/grouping-option`, { params })
      .pipe(
        map((res: ResponseModel<any>) => res.data)
      );
  }

  public deletePdfSettingsTemplate(name: string): Observable<null> {
    const params = { name };
    return this.http.delete<ResponseModel<null>>(this.apiUrl('/template'), { params })
      .pipe(
        // tap((res: ResponseModel<null>) => {
        // this.setStyles(res.data);
        // this.store.dispatch(LoadPdfSettingsSuccess({ pdfSettings: res.data }))
        // this.setCurrentPdfSettings(res.data);
        // }),
        map((res: ResponseModel<null>) => res.data)
      );
  }

  public getPdfSettingsTemplates(): Observable<PdfSettingsTemplateModel[]> {
    return this.http.get<ResponseModel<PdfSettingsTemplateModel[]>>(this.apiUrl('/all'))
      .pipe(
        // tap((res: ResponseModel<PdfSettingsTemplateModel[]>) => {
          // this.setStyles(res.data);
          // this.store.dispatch(LoadPdfSettingsSuccess({ pdfSettings: res.data }))
          // this.setCurrentPdfSettings(res.data);
        // }),
        map((res: ResponseModel<PdfSettingsTemplateModel[]>) => res.data)
      );
  }

  // public getCurrentPdfSettingsExportParams(settings: PdfSettingsModel): Observable<HttpResponse<Blob>> {
  //   return this.http.post(
  //     `${environment.javaApiVersion}/pdf/settings/preview`,
  //     {...settings},
  //     {
  //       observe: 'response',
  //       responseType: 'blob'
  //     }
  //   );
  // }

  public getUploadImageLogoLink(): string {
    return `${environment.javaApiUrl}${environment.javaApiVersion}/image/PDF_LOGO`;
  }

  public getUploadImageLogoBottomLink(): string {
    return `${environment.javaApiUrl}${environment.javaApiVersion}/image/PDF_IMG`;
  }

  private setCurrentPdfSettings(settings: PdfSettingsModel): void {
    this.currentPdfSettings$.next(settings);
  }

  public getCurrentPdfSettings$(): Observable<PdfSettingsModel> {
    return this.currentPdfSettings$.asObservable();
  }

  private setStyles(pdfSettingsModel: PdfSettingsModel): void {
    const { design, positions, header, footer } = pdfSettingsModel.settings;
    const headerGridColsWidth: {[key in PdfSettingsHeaderInfoBlocksEnum]: string} = {
      [PdfSettingsHeaderInfoBlocksEnum.LOGO]: '240px',
      [PdfSettingsHeaderInfoBlocksEnum.CUSTOMER]: '260px',
      [PdfSettingsHeaderInfoBlocksEnum.INFO]: '348px',
    };
    const headerGridTemplateColumns = header.headerInfo.order.map(itm => headerGridColsWidth[itm]).join(' ');
    const footerGridTemplateColumns = footer.footerInfo.showBankDetails
      ? 'repeat(4, 1fr)'
      : 'repeat(3, 1fr)';
    let roundingOuter = 0;
    let roundingInner = 0;

    this.document.documentElement.style.setProperty('--pdf-settings-main-color', design.mainColor);
    this.document.documentElement.style.setProperty('--pdf-settings-secondary-color', design.secondaryColor);
    this.document.documentElement.style.setProperty('--pdf-settings-headers-color', design.tableHeaderColor);
    this.document.documentElement.style.setProperty('--pdf-settings-discount-color', positions.highlightDiscountColor);
    this.document.documentElement.style.setProperty('--pdf-settings-amount-color', positions.highlightTotalAmountColor);
    this.document.documentElement.style.setProperty('--pdf-settings-header-grid-columns', headerGridTemplateColumns);
    this.document.documentElement.style.setProperty('--pdf-settings-footer-grid-columns', footerGridTemplateColumns);
    this.document.documentElement.style.setProperty('--pdf-settings-general-font-family', design.generalFont);
    this.document.documentElement.style.setProperty('--pdf-settings-heading-font-family', design.headerFont);
    if (design.style === PdfSettingsCornerStyleEnum.ANGULAR) {
      roundingOuter = 0;
      roundingInner = 0;
    } else {
      switch (design.rounding) {
        case PdfSettingsRoundingEnum.LIGHT:
          roundingOuter = 4;
          roundingInner = 2;
          break;
        case PdfSettingsRoundingEnum.MEDIUM:
          roundingOuter = 8;
          roundingInner = 6;
          break;
        case PdfSettingsRoundingEnum.STRONG:
          roundingOuter = 16;
          roundingInner = 12;
          break;
      }
    }
    // set default font sizes
    this.document.documentElement.style.setProperty('--pdf-settings-font-size', '14px');
    this.document.documentElement.style.setProperty('--pdf-settings-desc-font-size', '12px');
    this.document.documentElement.style.setProperty('--pdf-settings-heading-font-size', '20px');
    this.document.documentElement.style.setProperty('--pdf-settings-subheading-font-size', '16px');
    // reset to compact if needed
    if (design.useCompactFontSize) {
      this.document.documentElement.style.setProperty('--pdf-settings-font-size', '12px');
      this.document.documentElement.style.setProperty('--pdf-settings-desc-font-size', '10px');
      this.document.documentElement.style.setProperty('--pdf-settings-heading-font-size', '16px');
      this.document.documentElement.style.setProperty('--pdf-settings-subheading-font-size', '14px');
    }
    this.document.documentElement.style.setProperty('--pdf-settings-rounding-outer', roundingOuter + 'px');
    this.document.documentElement.style.setProperty('--pdf-settings-rounding-inner', roundingInner + 'px');
  }

}
